import axios, { InternalAxiosRequestConfig } from 'axios'; // Import InternalAxiosRequestConfig
// Assuming your auth store provides a way to get the current state synchronously
// OR you store the token in localStorage/sessionStorage. Adjust the import as needed.
import { useAuthStore } from '../store/authStore'; // Example: Using Zustand store

// Define the base URL for your backend API using environment variables
// Vite exposes env variables prefixed with VITE_ on import.meta.env
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'; // Fallback just in case

console.log(`API Base URL set to: ${API_BASE_URL}`); // For debugging

export const apiClient = axios.create({ // Add export here
  baseURL: API_BASE_URL, // Use the dynamic base URL
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // Increase default timeout to 30 seconds
});

// Store for reducing console noise
let lastLoggedStates = new Map<string, string>();

// --- Axios Request Interceptor ---
// This runs before every request made by apiClient
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    // Retrieve the token dynamically before each request
    // Option 1: Using Zustand store's synchronous getState()
    const token = useAuthStore.getState().accessToken;
    const isApiAuthReady = useAuthStore.getState().isApiAuthReady;

    // Option 2: Using localStorage (if you store the token there)
    // const token = localStorage.getItem('accessToken'); // Adjust 'accessToken' key if needed

    // Only log token availability for debugging, but reduce noise
    if (config.url?.includes('/session/') || config.url?.includes('/market/kline')) {
      // Reduce console noise for frequent calls, only log when token state changes
      const logKey = `${config.url}-token-${!!token}`;
      if (!lastLoggedStates.has(config.url) || lastLoggedStates.get(config.url) !== logKey) {
        console.log(`[Interceptor] Requesting ${config.url}. Token found: ${!!token}`);
        lastLoggedStates.set(config.url, logKey);
      }
    }

    if (token) {
      // Ensure headers object exists
      config.headers = config.headers ?? {};
      // Attach the Authorization header
      config.headers['Authorization'] = `Bearer ${token}`;
      
      // Only log for debugging when needed
      if (!isApiAuthReady || config.url?.includes('/session/')) {
        console.log('[Interceptor] Authorization header added.');
      }
    } else if (isApiAuthReady === false) {
      // During initial load, this is expected - don't spam warnings
      console.log(`[Interceptor] Initial auth in progress for ${config.url}, proceeding without token`);
    } else {
      console.warn('[Interceptor] No token found. Authorization header not added.');
      // Optionally, you could cancel the request here if a token is always required
      // delete config.headers['Authorization']; // Ensure it's removed if token disappears
    }
    return config; // Return the modified config
  },
  (error) => {
    // Handle request errors (e.g., network issues before sending)
    console.error('[Interceptor] Request error:', error);
    return Promise.reject(error);
  }
);


// Store reference to Auth0's getAccessTokenSilently function
let getAccessTokenSilentlyRef: ((options?: any) => Promise<string>) | null = null;

// Function to set the getAccessTokenSilently reference
export const setGetAccessTokenSilentlyRef = (getAccessTokenSilently: (options?: any) => Promise<string>) => {
  getAccessTokenSilentlyRef = getAccessTokenSilently;
};

// --- Axios Response Interceptor ---
apiClient.interceptors.response.use(
  (response) => {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do nothing, just return the response
    return response;
  },
  async (error) => {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    const originalRequest = error.config; // Get the original request config

    // Check for 401 Unauthorized error
    // Add a check to prevent retrying the same request indefinitely
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      console.warn('[Interceptor] Received 401 Unauthorized. Attempting token refresh...');
      originalRequest._retry = true; // Mark the request to prevent infinite loops

      const { logoutAuth0, setAccessToken } = useAuthStore.getState();

      // First attempt: Try to refresh the token using Auth0's getAccessTokenSilently
      if (getAccessTokenSilentlyRef) {
        try {
          console.log('[Interceptor] Attempting to refresh token via Auth0...');
          const newToken = await getAccessTokenSilentlyRef({
            cacheMode: 'off' // Force fresh token from Auth0
          });
          
          if (newToken) {
            console.log('[Interceptor] Token refreshed successfully. Updating store and retrying request...');
            setAccessToken(newToken); // Update the token in the store
            
            // Update the original request with the new token
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            
            // Retry the original request with the new token
            return apiClient(originalRequest);
          }
        } catch (refreshError) {
          console.warn('[Interceptor] Token refresh failed:', refreshError);
          // Continue to logout logic below
        }
      } else {
        console.warn('[Interceptor] getAccessTokenSilently reference not available. Cannot attempt token refresh.');
      }

      // If token refresh failed or is not available, proceed with logout
      if (typeof logoutAuth0 === 'function') {
        try {
          console.log('[Interceptor] Token refresh failed. Clearing access token and calling Auth0 logout.');
          setAccessToken(null); // Clear the token in the store

          // Perform the logout, redirecting back to the login page
          await logoutAuth0({
            logoutParams: {
              returnTo: window.location.origin + '/login' // Redirect to login page after logout
            }
          });

          // Reject with a specific error after logout is initiated
          return Promise.reject(new Error('Session expired and refresh failed. Redirecting to login.'));

        } catch (logoutError) {
          console.error('[Interceptor] Error during Auth0 logout:', logoutError);
          // Still reject the original error if logout fails
          return Promise.reject(error);
        }
      } else {
        console.error('[Interceptor] Logout function not available in store. Cannot perform automatic logout.');
        // Reject the original error if logout function isn't available
        return Promise.reject(error);
      }
    }

    // For any other errors (non-401 or already retried), just reject the promise
    console.error('[Interceptor] Response error:', error.response?.status, error.message);
    return Promise.reject(error);
  }
);


// --- Remove or Comment Out setGlobalAuthToken ---
// This function is no longer needed and can cause issues; the interceptor handles token attachment.
/*
export const setGlobalAuthToken = (token: string | null) => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log('Auth token set in apiClient'); // Optional: for debugging
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
    console.log('Auth token removed from apiClient'); // Optional: for debugging
  }
};
*/
// IMPORTANT: Make sure to remove any calls to setGlobalAuthToken in your application,
// typically found in your authentication setup/callback logic.

// --- TypeScript Interfaces (matching backend Pydantic models) ---

export interface PnlTimeframes {
  pnl_today: number;
  pnl_yesterday: number;
  pnl_last_7_days: number;
  pnl_last_30_days: number;
  pnl_last_90_days: number;
}

export interface AccountPnlSummaryItem {
  accountname: string;
  total_closedpnl: number;
}

export interface AccountPnlDailyItem {
  trade_date: string; // Dates are typically strings in JSON
  cumulative_closedpnl: number;
  accountname: string;
}

export interface StrategyPnlSummaryItem {
  strategyname: string;
  currentaccountname: string;
  total_pnl: number;
  net_profit_pct?: number | null;
  rank?: number;
}

export interface StrategyHealthMetricsItem {
  strategyname: string;
  currentaccountname: string;
  total_pnl: number;
  net_profit_pct?: number | null;
  total_score: number; // Health score (0-100)
  win_percentage?: number;
  profit_factor?: number;
  total_trades?: number;
  stability_status?: string;
  lifecycle_stage?: string;
  rank?: number;
  
  // Net Percentage Gain Implementation (August 18, 2025)
  current_period_starting_balance?: number | null;
  current_calculated_balance?: number | null;
  current_period_gain_pct?: number | null;
  days_in_current_account?: number | null;
  
  // Time-Weighted Return Implementation (August 18, 2025)
  time_weighted_return_pct?: number | null;
  
  // Data source tracking
  cached?: boolean;
  timestamp?: string;
}

export interface StrategyPnlDailyItem {
  trade_date: string; // Dates are typically strings in JSON
  cumulative_closedpnl: number;
  strategyname: string;
  currentaccountname: string;
  symbol?: string | null;
}

export interface MonthlyPnlItem {
  year: number;
  month: number;
  month_label: string;
  total_pnl: number;
  account_pnl: { [accountName: string]: number };
}

export interface AccountPnlBreakdown {
    accountname: string;
    pnl: number;
}

export interface LiveTrade {
    seq: number;
    symbol: string;
    side: 'Buy' | 'Sell';
    strategyname?: string;
    accountname: string;
    unrealisedPnl: string; // Keep as string if API returns string
    avgPrice: string;
    markPrice: string;
    takeProfit: string;
    stopLoss: string;
    createdTime: string; // Keep as string if API returns string (e.g., ISO format or timestamp)
    size: string;
    positionValue: string;
    leverage: string;
}

// --- Balance API Interfaces ---

export interface AssetBalance {
    asset: string;
    total: number;
    available: number;
    usd_value?: number | null;
}

export interface AccountBalance {
    account_name: string;
    account_type: string;
    balances: AssetBalance[];
    total_equity_usd?: number | null;
}

export interface BalanceError {
    account_name: string;
    error: string;
}

export interface BalancesResponse {
    data: AccountBalance[];
    errors: BalanceError[];
}

// --- Daily Balance History Interfaces ---

export interface AccountBalanceDailyItem {
  balance_date: string;  // ISO date (YYYY-MM-DD)
  account_name: string;
  balance_usdt: number;
  last_fetched?: string; // ISO datetime (optional)
}


// Define types based on backend Pydantic models
export interface TradeItem {
  id: number;
  accountname?: string | null;
  symbol?: string | null;
  side?: string | null;
  closedpnl?: number | null;
  avgentryprice?: number | null;
  createdtime?: string | null; // Changed from number | null (ISO Date String)
  updatedtime?: string | null; // Changed from number | null (ISO Date String)
  strategyname?: string | null;
  qty?: number | null;
  closedsize?: number | null;
  avgexitprice?: number | null;
  leverage?: number | null;
  // New fields for coverage analysis
  is_covered?: boolean | null;
  covered_by_rule_id?: number | null;
  coverage_notes?: string | null;
  // ROI calculation fields
  roi_percentage?: number | null;
  leveraged_roi_percentage?: number | null;
  position_value?: number | null;
  margin_used?: number | null;
}

export interface PaginatedTradesResponse {
  total_count: number;
  trades: TradeItem[];
}

// --- Trade Coverage Analyzer API Interfaces ---

export interface UncoveredTradesFilterPayload {
  account_name?: string | null;
  symbol?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

export interface UncoveredTradeDetail {
  trade_id: number;
  timestamp: string; // Or Date
  accountname: string;
  symbol: string;
  side: string;
  quantity: number;
  price: number;
  fee?: number | null; // Made optional
  pnl: number;
  // Add other relevant fields
}

// New/Updated interfaces based on backend response
export interface UncoveredSymbolSummary {
  symbol: string;
  uncovered_count: number;
}

export interface UncoveredAccountSummary {
  account_name: string;
  uncovered_count: number;
  symbols: UncoveredSymbolSummary[];
}

export interface UncoveredTradesSummary {
  total_uncovered_count: number;
  by_account: UncoveredAccountSummary[];
}

export interface PaginatedUncoveredTradesResponse {
  trades: UncoveredTradeDetail[];
  total_count: number;
  next_page_cursor?: string | null;
  // Potentially other pagination fields like 'limit', 'offset' if used
}

export interface TradeUpdateResponse {
  message: string;
  updated_trade_ids?: number[];
  // Add other relevant fields
}

// --- API Service Functions ---

const DEFAULT_TIMEZONE = 'UTC'; // Define default timezone

export const getPnlTimeframes = async (userTimezone: string | null): Promise<PnlTimeframes> => {
  const timezoneParam = userTimezone || DEFAULT_TIMEZONE;
  const response = await apiClient.get<PnlTimeframes>('/pnl/timeframes', {
    params: { user_timezone: timezoneParam } // Pass timezone
  });
  return response.data;
};

export const getAccountPnlSummary = async (): Promise<AccountPnlSummaryItem[]> => {
  const response = await apiClient.get<AccountPnlSummaryItem[]>('/pnl/accounts/summary');
  return response.data;
};

export const getAccountPnlDaily = async (userTimezone: string | null): Promise<AccountPnlDailyItem[]> => {
  const timezoneParam = userTimezone || DEFAULT_TIMEZONE;
  const response = await apiClient.get<AccountPnlDailyItem[]>('/pnl/accounts/daily', {
    params: { user_timezone: timezoneParam } // Pass timezone
  });
  return response.data;
};

export const getAccountBalancesDaily = async (
  userTimezone: string | null,
  dateFrom?: string | null,
  dateTo?: string | null
): Promise<AccountBalanceDailyItem[]> => {
  const timezoneParam = userTimezone || DEFAULT_TIMEZONE;
  const params: { [key: string]: string } = { user_timezone: timezoneParam };
  
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  
  console.log('Fetching daily balance data with params:', params);
  
  const response = await apiClient.get<AccountBalanceDailyItem[]>('/pnl/accounts/balances-daily', {
    params
  });
  
  console.log(`Successfully fetched ${response.data.length} daily balance records`);
  return response.data;
};

export const getStrategyPnlSummary = async (): Promise<StrategyPnlSummaryItem[]> => {
  const response = await apiClient.get<StrategyPnlSummaryItem[]>('/pnl/strategies/summary');
  return response.data;
};

export const getStrategyHealthMetrics = async (): Promise<StrategyHealthMetricsItem[]> => {
  const response = await apiClient.get('/strategies/optimized');
  
  // Transform the response to match our interface
  // Dashboard Strategy Ranking: Only shows active strategies (strategies currently running)
  // This excludes deactivated/historical strategies to focus on current performance
  const strategies = response.data.strategies || [];
  const responseTimestamp = response.data.timestamp; // This is now the actual analysis timestamp
  const isCached = response.data.cached;
  
  return strategies.map((strategy: any, index: number) => ({
    strategyname: strategy.strategyname,
    currentaccountname: strategy.currentaccountname,
    total_pnl: strategy.total_pnl || 0,
    net_profit_pct: strategy.net_profit_pct,
    total_score: strategy.total_score || 0,
    win_percentage: strategy.win_percentage,
    profit_factor: strategy.profit_factor,
    total_trades: strategy.total_trades,
    stability_status: strategy.stability?.status,
    lifecycle_stage: strategy.lifecycle?.stage,
    time_weighted_return_pct: strategy.time_weighted_return_pct, // Add TWR field mapping
    rank: index + 1, // Will be recalculated based on sorting
    cached: isCached,
    timestamp: strategy.generated_at || responseTimestamp // Use individual strategy timestamp or fallback to response timestamp
  }));
};

export const getStrategyPnlDaily = async (
    symbol: string | null | undefined, // Keep existing params
    account: string | null | undefined, // Keep existing params
    userTimezone: string | null // Add timezone param
): Promise<StrategyPnlDailyItem[]> => {
  const timezoneParam = userTimezone || DEFAULT_TIMEZONE;
  const params: { [key: string]: string } = { user_timezone: timezoneParam }; // Include timezone
  if (symbol) params.symbol = symbol;
  if (account) params.account = account;

  const response = await apiClient.get<StrategyPnlDailyItem[]>('/pnl/strategies/daily', { params });
  return response.data;
};

export const getMonthlyCalendar = async (userTimezone: string | null): Promise<MonthlyPnlItem[]> => {
  const timezoneParam = userTimezone || DEFAULT_TIMEZONE;
  const response = await apiClient.get<MonthlyPnlItem[]>('/pnl/calendar/monthly', {
    params: { user_timezone: timezoneParam } // Pass timezone
  });
  return response.data;
};

export const getOpenTrades = async (): Promise<LiveTrade[]> => {
    console.log("Attempting to fetch open positions via getOpenTrades in api.ts...");
    try {
        const response = await apiClient.get<LiveTrade[]>('/trades/open'); // Endpoint for open trades
        const data = response.data;

        if (!Array.isArray(data)) {
            console.error("API did not return an array for open trades:", data);
            throw new Error("Invalid data format received for open trades.");
        }
        console.log("Successfully fetched open trades:", data.length);
        return data;
    } catch (error: any) {
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch open trades';
        console.error("API Error fetching open trades:", errorMessage, error.response || error);
        throw new Error(errorMessage);
    }
};

/**
 * Fetches account balances from Bybit via the backend for the authenticated user.
 */
export const getAccountBalances = async (): Promise<BalancesResponse> => {
    console.log("Attempting to fetch account balances via getAccountBalances in api.ts...");
    try {
        // The backend endpoint is /api/balances, which is relative to the API_BASE_URL
        const response = await apiClient.get<BalancesResponse>('/balances');
        console.log("Successfully fetched account balances:", response.data);
        return response.data;
    } catch (error: any) {
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch account balances';
        console.error("API Error fetching account balances:", errorMessage, error.response || error);
        // Re-throw or handle as needed, maybe return a specific error structure
        // For now, re-throwing the error message
        throw new Error(errorMessage);
    }
};


// Function to fetch closed trades
export const fetchClosedTrades = async (
  filters: {
    symbol?: string | null;
    accountname?: string | null;
    strategyname?: string | null;
    limit?: number;
    offset?: number;
    sortBy?: string | null;
    sortOrder?: 'asc' | 'desc' | null;
  }
): Promise<PaginatedTradesResponse> => {
  const params = new URLSearchParams();
  if (filters.symbol && filters.symbol !== 'All') params.append('symbol', filters.symbol);
  if (filters.accountname && filters.accountname !== 'All') params.append('accountname', filters.accountname);
  if (filters.strategyname && filters.strategyname !== 'All') params.append('strategyname', filters.strategyname);
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.offset) params.append('offset', filters.offset.toString());
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

  try {
    const response = await apiClient.get<PaginatedTradesResponse>('/trades/closed', { params });
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch closed trades';
    console.error("API Error fetching closed trades:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

// Function to fetch a single trade by ID
export const fetchTradeById = async (tradeId: string | number): Promise<TradeItem | null> => {
  try {
    const response = await apiClient.get<TradeItem>(`/trades/by-id/${tradeId}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      console.warn(`Trade with ID ${tradeId} not found`);
      return null;
    }
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch trade';
    console.error("API Error fetching trade by ID:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

// Function to fetch distinct symbols, optionally filtered
export const fetchTradeSymbols = async (
  filters?: { accountname?: string | null; strategyname?: string | null }
): Promise<string[]> => {
  const params = new URLSearchParams();
  if (filters?.accountname && filters.accountname !== 'All') params.append('accountname', filters.accountname);
  if (filters?.strategyname && filters.strategyname !== 'All') params.append('strategyname', filters.strategyname);

  try {
    const response = await apiClient.get<string[]>('/trades/filters/symbols', { params });
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch trade symbols';
    console.error("API Error fetching trade symbols:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

// Function to fetch distinct accounts, optionally filtered
export const fetchTradeAccounts = async (
  filters?: { symbol?: string | null; strategyname?: string | null }
): Promise<string[]> => {
  const params = new URLSearchParams();
  if (filters?.symbol && filters.symbol !== 'All') params.append('symbol', filters.symbol);
  if (filters?.strategyname && filters.strategyname !== 'All') params.append('strategyname', filters.strategyname);

  try {
    const response = await apiClient.get<string[]>('/trades/filters/accounts', { params });
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch trade accounts';
    console.error("API Error fetching trade accounts:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

// Function to fetch distinct strategies, optionally filtered
export const fetchTradeStrategies = async (
  filters?: { symbol?: string | null; accountname?: string | null }
): Promise<string[]> => {
  const params = new URLSearchParams();
  if (filters?.symbol && filters.symbol !== 'All') params.append('symbol', filters.symbol);
  if (filters?.accountname && filters.accountname !== 'All') params.append('accountname', filters.accountname);

  try {
    const response = await apiClient.get<Array<string | null>>('/trades/filters/strategies', { params });
    // Ensure 'N/A' or similar handling if the backend returns nulls/empty strings for strategies
    const strategies = response.data;
    return strategies.map((s: string | null) => s || 'N/A').filter((value: string, index: number, self: string[]) => self.indexOf(value) === index); // Handle nulls and ensure uniqueness
  } catch (error: any) {
    console.error('Error fetching distinct strategies:', error.response ? error.response.data : error.message);
    throw error; // Re-throw to be handled by the caller
  }
};

// --- Trade Coverage Analyzer API Functions ---

/**
 * Interface for account+symbol trade patterns
 */
export interface AccountSymbolPattern {
  account_name: string;
  symbol: string;
  trade_count: number;
  first_trade_date: string;
  last_trade_date: string;
  total_pnl: number;
  win_rate: number;
  avg_position_size_usdt: number;
  predominant_side: string;
  suggested_strategy_name: string;
  date_range_days: number;
}

export interface TradePatternResponse {
  patterns: AccountSymbolPattern[];
}

/**
 * Fetches account+symbol trade patterns for smart strategy suggestions.
 */
export const getTradePatterns = async (minTrades: number = 5): Promise<TradePatternResponse> => {
  try {
    const response = await apiClient.get('/trades/patterns', {
      params: { min_trades: minTrades }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch trade patterns:', error);
    throw error;
  }
};

/**
 * Fetches a summary of uncovered trades.
 */
export const getUncoveredTradesSummary = async (
  filters?: UncoveredTradesFilterPayload
): Promise<UncoveredTradesSummary> => {
  try {
    // Ensure this is always a POST request as the backend expects filters in the body.
    // apiClient.post will send an empty JSON object {} if filters is undefined,
    // which is acceptable for an optional payload.
    const response = await apiClient.post('/trades/uncovered/summary', filters);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch uncovered trades summary:', error);
    // Rethrow the error so the calling component can handle it (e.g., display a message to the user)
    throw error;
  }
};

/**
 * Fetches a paginated list of uncovered trades.
 */
export const getUncoveredTrades = async (
  params: {
    filters?: UncoveredTradesFilterPayload;
    limit?: number;
    cursor?: string | undefined; // Changed from offset to cursor
  }
): Promise<PaginatedUncoveredTradesResponse> => {
  const queryParams = new URLSearchParams();
  if (params.filters?.symbol) queryParams.append('symbol', params.filters.symbol);
  if (params.filters?.account_name) queryParams.append('account_name', params.filters.account_name);
  if (params.filters?.start_date) queryParams.append('start_date', params.filters.start_date);
  if (params.filters?.end_date) queryParams.append('end_date', params.filters.end_date);
  if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
  if (params.cursor !== undefined) queryParams.append('cursor', params.cursor); // Use cursor

  console.log("Fetching uncovered trades with params:", queryParams.toString());
  try {
    const response = await apiClient.get<PaginatedUncoveredTradesResponse>('/trades/uncovered', { params: queryParams });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching uncovered trades:', error.response ? error.response.data : error.message);
    throw error;
  }
};

/**
 * Assigns a strategy to a list of trades.
 */
export const assignTradesToStrategy = async (
  payload: {
    trade_ids: number[];
    strategy_id?: number | null;
    new_strategy_name?: string | null;
  }
): Promise<TradeUpdateResponse> => {
  console.log("Assigning strategy to trades with payload:", payload);
  try {
    const response = await apiClient.post<TradeUpdateResponse>('/trades/assign_strategy', payload);
    return response.data;
  } catch (error: any) {
    console.error('Error assigning strategy to trades:', error.response ? error.response.data : error.message);
    throw error;
  }
};

/**
 * Marks trades as covered with a specific reason.
 */
export const markTradesAsCovered = async (
  payload: {
    trade_ids: number[];
    covered_reason: string;
  }
): Promise<TradeUpdateResponse> => {
  console.log("Marking trades as covered with payload:", payload);
  try {
    const response = await apiClient.post<TradeUpdateResponse>('/trades/mark_covered', payload);
    return response.data;
  } catch (error: any) {
    console.error('Error marking trades as covered:', error.response ? error.response.data : error.message);
    throw error;
  }
};


// --- Settings API Interfaces ---

// User Settings
export interface UserSettingsResponse {
  user_id: string;
  billing_email?: string | null;
  billing_name?: string | null;
  email_notifications: boolean;
  auto_renew: boolean;
  timezone?: string | null; // Add timezone field
  timezone_confirmed?: boolean; // New field
  updated_at?: string | null; // Assuming ISO string format from backend
  is_setup_complete: boolean;
}

export interface UserSettingsUpdate {
  billing_email?: string | null;
  billing_name?: string | null;
  email_notifications?: boolean;
  auto_renew?: boolean;
  timezone?: string | null; // Add timezone field
  timezone_confirmed?: boolean; // New field
}

// Subscription
export interface SubscriptionResponse {
  plan_type?: string | null;
  status?: string | null;
  current_period_end?: string | null; // Assuming ISO string format
  updated_at?: string | null;
}

// Linked Accounts
export interface UserAccountResponse {
  id: number;
  account_name: string;
  api_key: string; // Decrypted key
  exchange_name: string; // Add this line
  created_at?: string | null;
  updated_at?: string | null;
}

export interface UserAccountCreate {
  account_name: string;
  api_key: string;
  api_secret: string;
  exchange_name: string;
  passphrase?: string; // Required for Bitget, optional for others
}

export interface UserAccountUpdate {
  account_name?: string;
  api_key?: string;
  api_secret?: string;
  passphrase?: string; // Bitget only
}

// Bot Rules
export interface BotRuleResponse {
  rule_id: number;
  user_id: string;
  bot_name: string;
  account_name: string;
  symbol: string;
  start_date?: string | null;
  end_date?: string | null;
  starting_balance?: number | null;
  starting_balance_source?: string | null;
}

export interface BotRuleCreate {
  bot_name: string;
  account_name: string;
  symbol: string;
  start_date?: string | null;
  end_date?: string | null;
  starting_balance?: number | null;
}

export interface BotRuleUpdate {
  bot_name?: string;
  account_name?: string;
  symbol?: string;
  start_date?: string | null;
  end_date?: string | null;
  starting_balance?: number | null;
}


// --- API Service Functions ---

// User Settings
export const fetchUserSettings = async (): Promise<UserSettingsResponse> => {
  const response = await apiClient.get<UserSettingsResponse>('/v1/settings/user');
  return response.data;
};

export const updateUserSettings = async (data: UserSettingsUpdate): Promise<UserSettingsResponse> => {
  const response = await apiClient.put<UserSettingsResponse>('/v1/settings/user', data);
  return response.data;
};

// Timezone Confirmation
export interface TimezoneConfirmationRequest {
  timezone: string;
}

export interface TimezoneConfirmationResponse {
  success: boolean;
  timezone: string;
  timezone_confirmed: boolean;
}

// Confirm user's timezone during onboarding
export const confirmTimezone = async (timezone: string): Promise<TimezoneConfirmationResponse> => {
  const response = await apiClient.post<TimezoneConfirmationResponse>('/v1/settings/user/confirm-timezone', {
    timezone
  });
  return response.data;
};

// Subscription
export const fetchSubscription = async (): Promise<SubscriptionResponse> => {
  const response = await apiClient.get<SubscriptionResponse>('/v1/settings/subscription');
  return response.data;
};

// Linked Accounts
export const fetchLinkedAccounts = async (): Promise<UserAccountResponse[]> => {
  const response = await apiClient.get<UserAccountResponse[]>('/v1/settings/accounts');
  return response.data;
};

export const createLinkedAccount = async (data: UserAccountCreate): Promise<UserAccountResponse> => {
  const response = await apiClient.post<UserAccountResponse>('/v1/settings/accounts', data);
  return response.data;
};

export const updateLinkedAccount = async (accountId: number, data: UserAccountUpdate): Promise<UserAccountResponse> => {
  const response = await apiClient.put<UserAccountResponse>(`/v1/settings/accounts/${accountId}`, data);
  return response.data;
};

export const deleteLinkedAccount = async (accountId: number): Promise<void> => {
  await apiClient.delete(`/v1/settings/accounts/${accountId}`);
};

// Bot Rules
export interface BotRule {
  rule_id: number;
  strategy_id: number; // Added
  strategyname: string; // Still present from JOIN
  account_name: string;
  symbol: string;
  start_date: string | null; // ISO 8601 string
  end_date: string | null;   // ISO 8601 string
  status: 'Active' | 'Deactivated';
}

export interface BotRuleListResponse {
  bots: BotRule[];
}

// Removed TaskCreationResponse

export interface BotActionResponse {
  message: string;
  rule_id?: number | null; // rule_id is returned
  // task_info is removed
}

// Payloads for Mutations (Updated)
export interface AddBotPayload {
  strategy_id: number;
  account_name: string;
  symbol: string;
  start_date?: string | null; // ISO 8601 string for custom start date
  end_date?: string | null;   // ISO 8601 string for custom end date (null for active)
  starting_balance?: number | null; // Starting balance for net percentage calculations
  // Removed strategy detail fields
  // Removed create_task and allocation (handled differently now if needed)
}

export interface UpdateBotRulePayload {
    strategy_id?: number | null;
    account_name?: string | null;
    symbol?: string | null;
    start_date?: string | null; // ISO 8601 string or Date object
    end_date?: string | null;   // ISO 8601 string or Date object (null for active)
    is_active?: boolean | null; // Added is_active field
}

// Re-added Payloads for specific actions
export interface DeactivateBotPayload {
  create_task: boolean; // Flag to create a portfolio task
}

export interface ReactivateBotPayload {
  strategy_name: string; // Name of the strategy to reactivate
  symbol: string;        // Symbol for the new rule
  new_account: string;   // Account for the new rule
  create_task: boolean;  // Flag to create a portfolio task
  allocation?: number | null; // Optional allocation if task is created
}

export interface ChangeAccountPayload {
  new_account: string;   // The target account name
  create_task: boolean;  // Flag to create a portfolio task
  allocation?: number | null; // Optional allocation if task is created
}

// NEW: Bot Rule Overlap Validation Types
export interface BotRuleOverlapError {
  has_overlap: boolean;
  conflicting_rule_ids: number[];
  conflicting_strategies: string[];
  overlap_details?: string | null;
}

export interface BotRuleValidationResponse {
  is_valid: boolean;
  overlap_error?: BotRuleOverlapError | null;
  message: string;
}

// NEW: Conflict Detection Types (for trade analyzer safety net)
export interface ConflictedTradeDetail {
  trade_id: number;
  trade_timestamp: string;
  pnl: number;
  side: string;
  quantity: number;
  price: number;
  matching_rule_ids: number[];
  matching_strategies: string[];
  currently_assigned_strategy: string;
}

export interface ConflictSummary {
  account_name: string;
  symbol: string;
  rule_ids: number[];
  strategy_names: string[];
  affected_trades_count: number;
  overlap_start: string;
  overlap_end: string;
}

export interface ConflictsSummaryResponse {
  total_conflicts: number;
  conflicts: ConflictSummary[];
}


// --- Enhanced Trade Refresh API Interfaces ---
export interface RefreshRequest {
  force?: boolean;
  reason?: string;
}

export interface RefreshResponse {
  success: boolean;
  message: string;
  timestamp: string;
  changes_detected: boolean;
  trade_count?: number;
  last_trade_time?: string;
  cache_invalidated: boolean;
}

// --- API Service Functions ---

/**
 * Triggers the enhanced backend trade refresh with smart change detection.
 */
export const triggerUserTradeRefresh = async (request: RefreshRequest = {}): Promise<RefreshResponse> => {
  console.log("Triggering enhanced backend trade refresh for user...", request);
  try {
    const response = await apiClient.post<RefreshResponse>('/trades/refresh', request);
    console.log("Enhanced trade refresh completed:", response.data);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to trigger trade refresh';
    console.error("API Error triggering enhanced trade refresh:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Legacy function maintained for backward compatibility.
 */
export const triggerLegacyTradeRefresh = async (): Promise<{ status: string }> => {
  console.log("Triggering legacy backend trade refresh for user...");
  try {
    const response = await apiClient.post<{ status: string }>('/trades/refresh/legacy');
    console.log("Legacy backend trade refresh initiated:", response.data);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to trigger trade refresh';
    console.error("API Error triggering trade refresh:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Creates a new EventSource for trade updates with proper authentication
 */
export const createTradeUpdatesEventSource = (token: string): EventSource => {
  const apiUrl = import.meta.env.VITE_API_BASE_URL || '/api';
  const sseUrl = `${apiUrl}/trades/updates/stream?token=${encodeURIComponent(token)}`;
  
  console.log('Creating SSE connection for trade updates', { hasToken: !!token });
  return new EventSource(sseUrl);
};

/**
 * Get trade system health status
 */
export const getTradeSystemHealth = async (): Promise<any> => {
  try {
    const response = await apiClient.get('/trades/health');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching trade system health:', error);
    throw error;
  }
};

// --- Strategy Details API Interfaces ---
export interface StrategyDetailBase {
  strategyname: string;
  davidtechurl?: string | null;
  tradingviewurl?: string | null;
  targetwinrate?: number | null; // Use number for frontend, handle conversion if needed
  targetprofitfactor?: number | null;
  targetmaxdrawdown?: number | null;
  targetsharperatio?: number | null;
  targetsortinoratio?: number | null;
  targetexpecteddaily?: number | null;
  tradeleverage?: number | null;
  tradepercentofportfolio?: number | null;
  symbol?: string | null; // Added symbol field
  bot_known_balance?: number | null; // NEW: Bot balance field
}

export interface StrategyDetailCreate extends StrategyDetailBase {}

// Define all properties explicitly as optional and nullable
// Do not extend Partial<StrategyDetailBase>
export interface StrategyDetailUpdate {
    strategyname?: string | null | undefined;
    davidtechurl?: string | null | undefined;
    tradingviewurl?: string | null | undefined;
    targetwinrate?: number | null | undefined;
    targetprofitfactor?: number | null | undefined;
    targetmaxdrawdown?: number | null | undefined;
    targetsharperatio?: number | null | undefined;
    targetsortinoratio?: number | null | undefined;
    targetexpecteddaily?: number | null | undefined;
    tradeleverage?: number | null | undefined;
    tradepercentofportfolio?: number | null | undefined;
    symbol?: string | null | undefined; // Added symbol field
    bot_known_balance?: number | null | undefined; // NEW: Bot balance field
}

export interface StrategyDetail extends StrategyDetailBase {
  id: number;
  user_id: string;
  created_at: string; // ISO 8601 string
  balance_last_updated?: string; // NEW: Balance last updated timestamp
  // symbol is inherited from StrategyDetailBase
}

// --- Net Percentage Gain Implementation Interfaces (August 18, 2025) ---

export interface StrategyPerformanceHistory {
  account_name: string;
  start_date: string;
  end_date: string | null;
  starting_balance: number;
  calculated_ending_balance: number;
  period_gain_pct: number | null;
  period_status: 'Active' | 'Completed';
}

export interface StrategyNetGainMetrics {
  strategy_id: number;
  strategyname: string;
  current_account: string | null;
  current_period_starting_balance: number | null;
  current_balance: number | null;
  current_period_gain_pct: number | null;
  total_lifetime_gain_amount: number;
  average_gain_pct: number | null;
  total_periods: number;
  days_in_current_account: number | null;
  
  // Time-Weighted Return Implementation (August 18, 2025)
  time_weighted_return_pct: number | null;
}

// --- API Service Functions ---

// --- Strategy Details API Functions ---

/**
 * Fetches all strategy definitions for the authenticated user.
 */
export const fetchStrategies = async (): Promise<StrategyDetail[]> => {
  console.log("Attempting to fetch strategies...");
  try {
    const response = await apiClient.get<StrategyDetail[]>('/v1/strategy-details/');
    console.log(`Successfully fetched ${response.data.length} strategies.`);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch strategies';
    console.error("API Error fetching strategies:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Fetches a single strategy definition by ID.
 */
export const fetchStrategy = async (strategyId: number): Promise<StrategyDetail> => {
  console.log(`Attempting to fetch strategy ${strategyId}...`);
  try {
    const response = await apiClient.get<StrategyDetail>(`/v1/strategy-details/${strategyId}`);
    console.log(`Successfully fetched strategy ${strategyId}.`);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch strategy';
    console.error(`API Error fetching strategy ${strategyId}:`, errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Creates a new strategy definition.
 */
export const createStrategy = async (payload: StrategyDetailCreate): Promise<StrategyDetail> => {
  console.log("Attempting to create strategy:", payload);
  try {
    const response = await apiClient.post<StrategyDetail>('/v1/strategy-details', payload);
    console.log("Successfully created strategy:", response.data);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to create strategy';
    console.error("API Error creating strategy:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Updates an existing strategy definition.
 */
// The payload type here now matches the updated StrategyDetailUpdate interface
export const updateStrategy = async (strategyId: number, payload: StrategyDetailUpdate): Promise<StrategyDetail> => {
  console.log(`Attempting to update strategy ${strategyId}:`, payload);
  try {
    const response = await apiClient.put<StrategyDetail>(`/strategy-details/${strategyId}`, payload);
    console.log(`Successfully updated strategy ${strategyId}:`, response.data);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to update strategy';
    console.error(`API Error updating strategy ${strategyId}:`, errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Deletes a strategy definition.
 */
export const deleteStrategy = async (strategyId: number): Promise<void> => {
  console.log(`Attempting to delete strategy ${strategyId}...`);
  try {
    // Ensure the correct endpoint is used, including the trailing slash if needed by FastAPI
    await apiClient.delete(`/v1/strategy-details/${strategyId}`);
    console.log(`Successfully deleted strategy ${strategyId}.`);
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete strategy';
    console.error(`API Error deleting strategy ${strategyId}:`, errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

// --- Net Percentage Gain API Functions (August 18, 2025) ---

/**
 * Fetches complete performance history for a strategy across all accounts.
 */
export const fetchStrategyPerformanceHistory = async (strategyId: number): Promise<StrategyPerformanceHistory[]> => {
  console.log(`Attempting to fetch performance history for strategy ${strategyId}...`);
  try {
    const response = await apiClient.get<StrategyPerformanceHistory[]>(`/v1/strategies/${strategyId}/performance-history`);
    console.log(`Successfully fetched performance history for strategy ${strategyId}.`);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch strategy performance history';
    console.error(`API Error fetching performance history for strategy ${strategyId}:`, errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Fetches net percentage gains for all user strategies.
 */
export const fetchAllStrategiesNetGains = async (): Promise<StrategyNetGainMetrics[]> => {
  console.log("Attempting to fetch net percentage gains for all strategies...");
  try {
    const response = await apiClient.get<StrategyNetGainMetrics[]>('/v1/strategies/net-percentage-gains');
    console.log(`Successfully fetched net gains for ${response.data.length} strategies.`);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch strategy net gains';
    console.error("API Error fetching strategy net gains:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};


// --- Bot Maintenance API Interfaces (Updated) --

export interface BotRule {
  rule_id: number;
  strategy_id: number; // Added
  strategyname: string; // Still present from JOIN
  account_name: string;
  symbol: string;
  start_date: string | null; // ISO 8601 string
  end_date: string | null;   // ISO 8601 string
  status: 'Active' | 'Deactivated';
}

export interface BotRuleListResponse {
  bots: BotRule[];
}

// Removed TaskCreationResponse

export interface BotActionResponse {
  message: string;
  rule_id?: number | null; // rule_id is returned
  // task_info is removed
}

// Payloads for Mutations (Updated)
export interface AddBotPayload {
  strategy_id: number;
  account_name: string;
  symbol: string;
  // Removed strategy detail fields
  // Removed create_task and allocation (handled differently now if needed)
}

export interface UpdateBotRulePayload {
    strategy_id?: number | null;
    account_name?: string | null;
    symbol?: string | null;
    start_date?: string | null; // ISO 8601 string or Date object
    end_date?: string | null;   // ISO 8601 string or Date object (null for active)
    is_active?: boolean | null; // Added is_active field
}

// Re-added Payloads for specific actions
export interface DeactivateBotPayload {
  create_task: boolean; // Flag to create a portfolio task
}

export interface ReactivateBotPayload {
  strategy_name: string; // Name of the strategy to reactivate
  symbol: string;        // Symbol for the new rule
  new_account: string;   // Account for the new rule
  create_task: boolean;  // Flag to create a portfolio task
  allocation?: number | null; // Optional allocation if task is created
}

export interface ChangeAccountPayload {
  new_account: string;   // The target account name
  create_task: boolean;  // Flag to create a portfolio task
  allocation?: number | null; // Optional allocation if task is created
}


// --- API Service Functions ---

/**
 * Fetches all bot rules (active and inactive) for the authenticated user.
 * Includes strategy_id and strategyname.
 */
export const fetchAllBotRules = async (): Promise<BotRuleListResponse> => {
  console.log("Attempting to fetch bot rules via fetchAllBotRules in api.ts...");
  try {
    // Endpoint prefix is now /api (added in router definition)
    const response = await apiClient.get<BotRuleListResponse>('/bots/rules');
    console.log("Successfully fetched bot rules:", response.data.bots.length);
    // Ensure dates are handled correctly if needed (e.g., convert to Date objects)
    // response.data.bots.forEach(bot => {
    //   bot.start_date = bot.start_date ? new Date(bot.start_date) : null;
    //   bot.end_date = bot.end_date ? new Date(bot.end_date) : null;
    // });
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch bot rules';
    console.error("API Error fetching bot rules:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Adds a new bot rule, linking it to an existing strategy.
 */
export const addBotRule = async (payload: AddBotPayload): Promise<BotActionResponse> => {
  console.log("Attempting to add bot rule:", payload);
  try {
    // Endpoint prefix is now /api
    const response = await apiClient.post<BotActionResponse>('/bots/rules', payload);
    console.log("Successfully added bot rule:", response.data);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to add bot rule';
    console.error("API Error adding bot rule:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Updates an existing bot rule.
 */
export const updateBotRule = async (ruleId: number, payload: UpdateBotRulePayload): Promise<BotActionResponse> => {
    console.log(`Attempting to update bot rule ${ruleId}:`, payload);
    try {
        // Endpoint prefix is now /api
        const response = await apiClient.put<BotActionResponse>(`/bots/rules/${ruleId}`, payload);
        console.log(`Successfully updated bot rule ${ruleId}:`, response.data);
        return response.data;
    } catch (error: any) {
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to update bot rule';
        console.error(`API Error updating bot rule ${ruleId}:`, errorMessage, error.response || error);
        throw new Error(errorMessage);
    }
};


/**
 * Deactivates a specific bot rule by setting its end_date.
 * Accepts a payload.
 */
export const deactivateBotRule = async (ruleId: number, payload: DeactivateBotPayload): Promise<BotActionResponse> => {
  console.log(`Attempting to deactivate bot rule ${ruleId} with payload:`, payload);
  try {
    // Endpoint prefix is now /api
    // Use PUT and send the payload in the body
    const response = await apiClient.put<BotActionResponse>(`/bots/rules/${ruleId}/deactivate`, payload);
    console.log(`Successfully deactivated bot rule ${ruleId}:`, response.data);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to deactivate bot rule';
    console.error(`API Error deactivating bot rule ${ruleId}:`, errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Reactivates a strategy by creating a new bot rule.
 * Since there's no dedicated reactivate endpoint, this uses the add bot rule endpoint.
 * Note: This function expects the strategy_id to be provided in the payload or requires
 * a separate lookup to convert strategy_name to strategy_id.
 */
export const reactivateBotRule = async (payload: ReactivateBotPayload): Promise<BotActionResponse> => {
  console.log("Attempting to reactivate bot rule with payload:", payload);
  try {
    // First, we need to find the strategy_id from the strategy_name
    // This requires fetching strategies and finding the matching one
    const strategies = await fetchStrategies();
    const strategy = strategies.find(s => s.strategyname === payload.strategy_name);
    
    if (!strategy) {
      throw new Error(`Strategy '${payload.strategy_name}' not found`);
    }
    
    // Map ReactivateBotPayload to AddBotPayload since reactivation creates a new rule
    const addPayload: AddBotPayload = {
      strategy_id: strategy.id,
      account_name: payload.new_account,
      symbol: payload.symbol
    };
    
    // Use the existing add bot rule endpoint instead of the non-existent reactivate endpoint
    const response = await apiClient.post<BotActionResponse>('/bots/rules', addPayload);
    console.log("Successfully reactivated bot rule:", response.data);
    
    // Note: The create_task and allocation functionality from the original payload
    // is not handled in this implementation. If task creation is needed,
    // it should be implemented separately or the backend should be extended
    // to handle these additional parameters in the add rule endpoint.
    
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to reactivate bot rule';
    console.error("API Error reactivating bot rule:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Changes the account for a bot rule by updating it.
 */
export const changeAccountRule = async (ruleId: number, payload: ChangeAccountPayload): Promise<BotActionResponse> => {
  console.log(`Attempting to change account for bot rule ${ruleId} with payload:`, payload);
  try {
    // Use the existing update endpoint instead of the non-existent change-account endpoint
    const updatePayload: UpdateBotRulePayload = {
      account_name: payload.new_account
    };
    
    const response = await apiClient.put<BotActionResponse>(`/bots/rules/${ruleId}`, updatePayload);
    console.log(`Successfully changed account for bot rule ${ruleId}:`, response.data);
    
    // Note: The create_task and allocation functionality from the original payload
    // is not handled in this simple update. If task creation is needed,
    // it should be implemented separately or the backend should be extended
    // to handle these additional parameters in the update endpoint.
    
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to change bot account';
    console.error(`API Error changing account for bot rule ${ruleId}:`, errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Permanently deletes a bot rule.
 */
export const deleteBotRule = async (ruleId: number): Promise<void> => {
  console.log(`Attempting to permanently delete bot rule ${ruleId}...`);
  try {
    // Endpoint prefix is /api
    await apiClient.delete(`/bots/rules/${ruleId}`);
    console.log(`Successfully deleted bot rule ${ruleId}.`);
    // No data is returned on 204 No Content
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete bot rule';
    console.error(`API Error deleting bot rule ${ruleId}:`, errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

// --- NEW: Bot Rule Validation API Functions ---

/**
 * Validates a bot rule before creation, checking for overlaps with existing rules.
 * This is used for real-time validation in the frontend form.
 */
export const validateBotRule = async (payload: AddBotPayload): Promise<BotRuleValidationResponse> => {
  console.log("Validating bot rule:", payload);
  try {
    const response = await apiClient.post<BotRuleValidationResponse>('/bots/rules/validate', payload);
    console.log("Bot rule validation result:", response.data);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to validate bot rule';
    console.error("API Error validating bot rule:", errorMessage, error.response || error);
    // Return a safe failure state
    return {
      is_valid: false,
      overlap_error: null,
      message: errorMessage
    };
  }
};

/**
 * Fetches a summary of existing bot rule conflicts (for safety net detection).
 * This should return minimal/no conflicts if the prevention system is working correctly.
 */
export const getConflictsSummary = async (): Promise<ConflictsSummaryResponse> => {
  console.log("Fetching conflicts summary for safety net...");
  try {
    // Note: This endpoint would need to be implemented in the backend
    // For now, we'll use the existing overlaps detection function via a custom endpoint
    const response = await apiClient.get<ConflictsSummaryResponse>('/trades/conflicts/summary');
    console.log("Conflicts summary result:", response.data);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch conflicts summary';
    console.error("API Error fetching conflicts summary:", errorMessage, error.response || error);
    // Return empty conflicts as fallback
    return {
      total_conflicts: 0,
      conflicts: []
    };
  }
};

/**
 * Fetches detailed conflicted trades for a specific account/symbol combination.
 * Used by the cleanup wizard when conflicts are detected.
 */
export const getConflictedTrades = async (
  accountName: string,
  symbol: string,
  limit: number = 50,
  offset: number = 0
): Promise<ConflictedTradeDetail[]> => {
  console.log(`Fetching conflicted trades for ${accountName}/${symbol}...`);
  try {
    const queryParams = new URLSearchParams({
      account_name: accountName,
      symbol: symbol,
      limit: limit.toString(),
      offset: offset.toString()
    });
    
    const response = await apiClient.get<ConflictedTradeDetail[]>(`/trades/conflicts?${queryParams}`);
    console.log("Conflicted trades result:", response.data);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch conflicted trades';
    console.error("API Error fetching conflicted trades:", errorMessage, error.response || error);
    return [];
  }
};


// --- Market Data API Interfaces ---

export interface MarketKlineItem {
  x: number; // Timestamp (ms)
  o: number; // Open
  h: number; // High
  l: number; // Low
  c: number; // Close
}

// --- Portfolio Forecast API Interfaces ---

export type Periodicity = "daily" | "weekly" | "monthly";

// Request schema remains the same
export interface HistoricalMetricsRequest {
  periodicity: Periodicity;
  account_names?: string[] | null;
  strategy_names?: string[] | null;
  start_date?: string | null;
  end_date?: string | null;
}

// New interface for individual performance data
export interface StrategyAccountPerformance {
    strategyname: string;
    accountname: string;
    average_periodic_pnl: number;
    total_trades: number;
}

// Updated response schema
export interface HistoricalMetricsResponse {
  performance_data: StrategyAccountPerformance[]; // List of performance details
  request_periodicity: Periodicity; // The periodicity used for calculations
  request_start_date: string | null; // Reflect requested dates
  request_end_date: string | null;   // Reflect requested dates
  message?: string | null; // For errors or notes like "No trades found"
}

// --- Interfaces for Filter Options ---
export interface AvailableAccountsResponse {
  accounts: string[];
}

export interface AvailableStrategiesResponse {
  strategies: string[];
}

// --- API Service Functions ---

/**
 * Fetches historical Kline (OHLCV) data for a given symbol and interval.
 */
export const getMarketKline = async (
  symbol: string,
  interval: string = '15', // Default interval
  limit: number = 100     // Default limit
): Promise<MarketKlineItem[]> => {
  console.log(`Fetching Kline data for ${symbol}, interval ${interval}, limit ${limit}`);
  try {
    // Use the correct endpoint path including the prefix defined in the router
    const response = await apiClient.get<MarketKlineItem[]>('/market/kline', {
      params: {
        symbol,
        interval,
        limit,
      },
    });
    console.log(`Successfully fetched ${response.data.length} Kline points for ${symbol}.`);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch market Kline data';
    console.error(`API Error fetching Kline data for ${symbol}:`, errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};


/**
 * Fetches historical performance metrics for portfolio forecasting.
 * Now returns detailed data per strategy/account.
 */
export const fetchHistoricalMetrics = async (params: HistoricalMetricsRequest): Promise<HistoricalMetricsResponse> => {
  // Use URLSearchParams to handle multiple values for the same key correctly
  const searchParams = new URLSearchParams();

  // Append periodicity
  searchParams.append('periodicity', params.periodicity);

  // Append account_names if present
  if (params.account_names && params.account_names.length > 0) {
    params.account_names.forEach(name => searchParams.append('account_names', name));
  }

  // Append strategy_names if present
  if (params.strategy_names && params.strategy_names.length > 0) {
    params.strategy_names.forEach(name => searchParams.append('strategy_names', name));
  }

  // Append dates if present
  if (params.start_date) {
    searchParams.append('start_date', params.start_date);
  }
  if (params.end_date) {
    searchParams.append('end_date', params.end_date);
  }


  console.log("Fetching detailed historical metrics with query params:", searchParams.toString()); // Log the actual params string
  try {
    // Pass URLSearchParams directly to axios params
    // The response type is now the updated HistoricalMetricsResponse
    const response = await apiClient.get<HistoricalMetricsResponse>('/v1/portfolio/historical-metrics', { params: searchParams });
    console.log("Received detailed historical metrics:", response.data);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching detailed historical metrics:", error.response?.data || error.message);
    // Re-throw the error so react-query can handle it
    throw error;
  }
};

// --- Functions for Filter Options ---

export const fetchAvailableAccounts = async (): Promise<AvailableAccountsResponse> => {
  console.log("Fetching available accounts...");
  try {
    const response = await apiClient.get<AvailableAccountsResponse>('/v1/portfolio/available-accounts');
    console.log("Received available accounts:", response.data);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching available accounts:", error.response?.data || error.message);
    throw error;
  }
};

export const fetchAvailableStrategies = async (): Promise<AvailableStrategiesResponse> => {
  console.log("Fetching available strategies...");
  try {
    const response = await apiClient.get<AvailableStrategiesResponse>('/v1/portfolio/available-strategies');
    console.log("Received available strategies:", response.data);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching available strategies:", error.response?.data || error.message);
    throw error;
  }
};

// --- Portfolio Command Center API Interfaces ---

export interface PortfolioHealthOverview {
  strategies_on_target_pct: number;
  avg_win_rate_vs_target: number;
  avg_profit_factor_vs_target: number;
  total_portfolio_pnl: number;
}

export interface ActionItem {
  strategy_name: string;
  current_account: string;
  symbol: string;
  action_message: string;
  action_icon: string;
  move_action?: string | null;
  move_icon?: string | null;
  move_reason?: string | null;
  priority: number;
  win_rate: number;
  target_win_rate: number;
  win_rate_diff: number;
  profit_factor: number;
  target_profit_factor: number;
  profit_factor_diff: number;
  lifecycle_stage: string;
  lifecycle_icon: string;
}

export interface StrategyHealthScore {
  strategy_name: string;
  account: string;
  symbol: string;
  total_score: number;
  health_status: string;
  health_color: string;
  performance_score: number;
  stability_score: number;
  lifecycle_score: number;
  regime_fit_score: number;
  win_rate: number;
  target_win_rate: number;
  profit_factor: number;
  target_profit_factor: number;
  trade_count: number;
  total_pnl: number;
  lifecycle_stage: string;
  lifecycle_icon: string;
}

export interface MarketRegimeInfo {
  symbol: string;
  current_regime: string;
  volatility?: number | null;
  regime_color?: string | null;
}

export interface StrategyRegimePerformance {
  strategy_name: string;
  symbol: string;
  regime: string;
  win_rate?: number | null;
  avg_pnl?: number | null;
  total_trades?: number | null;
}

export interface AllocationRecommendation {
  strategy_name: string;
  symbol: string;
  current_regime: string;
  win_rate_in_regime: number;
  win_rate_vs_target_diff: number;
  recommendation: string;
  recommendation_icon: string;
  recommendation_color: string;
  requires_balance_update?: boolean;
  current_bot_balance?: number | null;
  recommended_bot_balance?: number | null;
  balance_last_updated?: string | null;
  balance_update_rationale?: string | null;
}

export interface ProjectedAllocation {
  strategy_name: string;
  symbol: string;
  projected_account: string;
  health_score: number;
  current_trade_pct: number;
  recommended_trade_pct: number;
  recommended_position_size: number;
  leverage: number;
  rationale: string;
  min_size_adjusted: boolean;
}

export interface UnifiedAction {
  action_id?: string; // Made optional as it's not in the sample response
  priority: number;
  action_type?: string | null; // Made optional
  strategy_name?: string | null;
  symbol?: string | null;
  action_description: string; // Changed from details
  reason: string;
  icon: string;
  current_account?: string | null; // Added
  swap_partner_strategy_name?: string | null; // Changed from swap_partner
  is_part_of_swap?: boolean | null; // Added
  role_in_swap?: string | null; // Added
  swap_group_id?: string | null; // Added
  allocation_change_for_this_strategy?: Record<string, any> | null; // Changed from allocation_change_details
}

export interface PortfolioCommandCenterResponse {
  portfolio_health_overview: PortfolioHealthOverview; // Renamed from health_overview
  // action_items: ActionItem[]; // Removed, not in actual response
  strategy_health_scores: StrategyHealthScore[]; // Renamed from strategy_scores
  market_regime_info: MarketRegimeInfo[]; // Renamed from market_regimes
  regime_performance: StrategyRegimePerformance[];
  allocation_recommendations: AllocationRecommendation[];
  projected_allocations: ProjectedAllocation[];
  unified_actions: UnifiedAction[];
}

// --- API Service Functions ---

export const fetchPortfolioCommandCenterData = async (): Promise<PortfolioCommandCenterResponse> => {
  console.log("Fetching Portfolio Command Center data...");
  try {
    const response = await apiClient.get<PortfolioCommandCenterResponse>('/portfolio-command/');
    console.log("Successfully fetched Portfolio Command Center data.");
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch Portfolio Command Center data';
    console.error("API Error fetching Portfolio Command Center data:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Fetches the portfolio health overview metrics.
 */
export const fetchPortfolioOverview = async (): Promise<PortfolioHealthOverview> => {
  console.log("Fetching portfolio overview...");
  try {
    const response = await apiClient.get<PortfolioHealthOverview>('/portfolio-command/overview');
    console.log("Successfully fetched portfolio overview.");
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch portfolio overview';
    console.error("API Error fetching portfolio overview:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Fetches the list of action alerts for strategies needing attention.
 */
export const fetchActionAlerts = async (): Promise<ActionItem[]> => {
  console.log("Fetching action alerts...");
  try {
    const response = await apiClient.get<ActionItem[]>('/portfolio-command/alerts');
    console.log(`Successfully fetched ${response.data.length} action alerts.`);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch action alerts';
    console.error("API Error fetching action alerts:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Fetches the health scorecard for all active strategies.
 */
export const fetchStrategyScorecard = async (): Promise<StrategyHealthScore[]> => {
  console.log("Fetching strategy scorecard...");
  try {
    const response = await apiClient.get<StrategyHealthScore[]>('/portfolio-command/scorecard');
    console.log(`Successfully fetched ${response.data.length} strategy scores.`);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch strategy scorecard';
    console.error("API Error fetching strategy scorecard:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Fetches the current market regime information for relevant symbols.
 */
export const fetchMarketRegimes = async (): Promise<MarketRegimeInfo[]> => {
  console.log("Fetching market regimes...");
  try {
    const response = await apiClient.get<MarketRegimeInfo[]>('/portfolio-command/market-regimes');
    console.log(`Successfully fetched ${response.data.length} market regimes.`);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch market regimes';
    console.error("API Error fetching market regimes:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Fetches strategy performance metrics broken down by market regime.
 */
export const fetchStrategyRegimePerformance = async (strategyName?: string | null): Promise<StrategyRegimePerformance[]> => {
  console.log(`Fetching strategy regime performance for strategy: ${strategyName || 'All'}...`);
  try {
    const params = new URLSearchParams();
    if (strategyName) {
      params.append('strategy_name', strategyName);
    }
    const response = await apiClient.get<StrategyRegimePerformance[]>('/portfolio-command/regime-performance', { params });
    console.log(`Successfully fetched ${response.data.length} regime performance records.`);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch strategy regime performance';
    console.error("API Error fetching strategy regime performance:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Fetches allocation recommendations based on market regime performance.
 */
export const fetchAllocationRecommendations = async (): Promise<AllocationRecommendation[]> => {
  console.log("Fetching allocation recommendations...");
  try {
    const response = await apiClient.get<AllocationRecommendation[]>('/portfolio-command/allocation-recommendations');
    console.log(`Successfully fetched ${response.data.length} allocation recommendations.`);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch allocation recommendations';
    console.error("API Error fetching allocation recommendations:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Fetches projected allocations after applying recommended actions.
 */
export const fetchProjectedAllocations = async (): Promise<ProjectedAllocation[]> => {
  console.log("Fetching projected allocations...");
  try {
    const response = await apiClient.get<ProjectedAllocation[]>('/portfolio-command/projected-allocations');
    console.log(`Successfully fetched ${response.data.length} projected allocations.`);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch projected allocations';
    console.error("API Error fetching projected allocations:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Fetches the consolidated and prioritized list of actions to take.
 */
export const fetchUnifiedActions = async (): Promise<UnifiedAction[]> => {
  console.log("Fetching unified actions...");
  try {
    const response = await apiClient.get<UnifiedAction[]>('/portfolio-command/unified-actions');
    console.log(`Successfully fetched ${response.data.length} unified actions.`);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch unified actions';
    console.error("API Error fetching unified actions:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Generates comprehensive portfolio actions and command center data.
 */
export const generateComprehensivePortfolioActions = async (): Promise<PortfolioCommandCenterResponse> => {
  console.log("Generating comprehensive portfolio actions...");
  try {
    const response = await apiClient.post<PortfolioCommandCenterResponse>('/portfolio-command/generate-actions');
    console.log("Successfully generated comprehensive portfolio actions.");
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to generate comprehensive portfolio actions';
    console.error("API Error generating comprehensive portfolio actions:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Creates a Stripe Customer Portal session for the authenticated user.
 */
export const createCustomerPortalSession = async (): Promise<{ url: string }> => {
  console.log("Requesting Stripe Customer Portal session URL...");
  try {
    // The backend endpoint is POST /api/subscriptions/create-customer-portal-session
    // Ensure the router prefix is included if necessary (e.g., /api/subscriptions)
    // Prepend /api to the path /api/v1/subscriptions
    const response = await apiClient.post<{ url: string }>('/v1/subscriptions/create-customer-portal-session');
    console.log("Successfully received Stripe Customer Portal URL.");
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to create customer portal session';
    console.error("API Error creating customer portal session:", errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

// --- Admin Impersonation Types ---
export interface AdminUser {
  user_id: string;
  email: string; // Primary email string (from backend: billing_email or user_id as fallback)
  billing_email?: string | null; // Actual billing_email, if present
  full_name?: string | null; // User's full name
  roles: string[];
  // Add other relevant fields if your backend provides them e.g. last_login, created_at
}

export interface ImpersonateStartResponse {
  impersonation_token: string;
  expires_at: string; // ISO date string for when the token expires
}

// --- Admin Impersonation API Functions ---

/**
 * Search users with type-ahead support for admin features.
 * Requires admin privileges.
 * @param query Search query (minimum 2 characters)
 * @param limit Maximum number of results (default 20)
 */
export const searchAdminUsers = async (query: string, limit: number = 20): Promise<AdminUser[]> => {
  const { data } = await apiClient.get<AdminUser[]>('/admin/users/search', {
    params: { q: query, limit }
  });
  return data;
};

/**
 * Fetches a list of users for admin impersonation purposes.
 * Requires admin privileges.
 * DEPRECATED: Use searchAdminUsers for better scalability.
 */
export const fetchAdminUsers = async (): Promise<AdminUser[]> => {
  const { data } = await apiClient.get<AdminUser[]>('/admin/users');
  return data;
};

/**
 * Initiates an impersonation session for a target user.
 * Requires admin privileges.
 * @param targetUserId The ID of the user to impersonate.
 */
export const startImpersonation = async (targetUserId: string): Promise<ImpersonateStartResponse> => {
  const { data } = await apiClient.post<ImpersonateStartResponse>('/admin/impersonate/start', { target_user_id: targetUserId });
  return data;
};

// Portfolio Command Center interfaces and functions
export interface LifecycleStage {
  stage: string;
  icon: string;
  confidence_modifier: number;
  allocation_cap: number;
  description: string;
}

export interface ActionTiming {
  action_ready: boolean;
  next_review?: string;
  lookback_days?: number;
  last_action?: string;
}

export interface PriorityAction {
  // Source recommendation
  recommendation_id: string;
  
  // Strategy identification
  strategy_name: string;
  account_name: string;
  strategy: string;
  account: string;
  symbol: string;
  
  // Action details
  action_type: string;
  action: string;  // Legacy field - use performance_status instead
  performance_status?: string;  // Enhanced field from Phase 0.5
  action_icon: string;
  reason: string;
  move_action: string;
  move_icon: string;
  move_reason: string;
  target_account: string | null;
  current_account: string;
  
  // Priority and urgency
  priority_level: number;
  priority: number;
  urgency: number;
  expected_impact: string;
  risk_level: string;
  
  // Performance metrics
  win_percentage: number;
  target_win_rate: number;
  profit_factor: number;
  target_profit_factor: number;
  total_trades: number;
  trade_count: number;
  total_pnl: number;
  net_profit_pct: number;
  return_pct: string;
  
  // Status and lifecycle
  stability_status: string;
  stability_icon: string;
  stability_color: string;
  lifecycle_stage: string;
  lifecycle_icon: string;
  lifecycle: {
    stage: string;
    icon: string;
    allocation_cap: number;
    confidence_modifier: number;
  };
  
  // Allocation data
  current_allocation: number;
  recommended_allocation: number;
  allocation_change: number;
  allocation_direction: string;
  allocation_rationale: string;
  allocation_rationale_detailed?: any;  // Enhanced allocation breakdown from Phase 3
  current_position_size: number;
  recommended_position_size: number;
  position_change: number;
  
  // Balance update fields (from Balance Update Feature)
  requires_balance_update?: boolean;
  current_bot_balance?: number | null;
  recommended_bot_balance?: number | null;
  balance_discrepancy?: number | null;  // Difference between current and recommended balance
  balance_last_updated?: string | null;
  balance_update_rationale?: string | null;
  
  // NEW: Balance update timing fields (independent timing component)
  balance_ready_for_execution?: boolean;
  balance_execution_window_start?: string | null;
  balance_execution_window_end?: string | null;
  balance_cooldown_until?: string | null;
  balance_next_review_date?: string | null;
  balance_last_action_date?: string | null;
  balance_days_since_last_action?: number | null;
  balance_urgency_level?: string;
  balance_decision_confidence?: number;
  
  // Enhanced rationale from Phase 2
  score_breakdown?: {
    performance_score: number;
    stability_score: number;
    lifecycle_score: number;
    regime_fit_score: number;
    total_score: number;
    // Max values for accurate progress bars (optional for backward compatibility)
    performance_max?: number;
    stability_max?: number;
    lifecycle_max?: number;
    regime_fit_max?: number;
  };
  stability_status_rationale?: string;
  action_decision_rationale?: string;
  
  // Scoring
  health_score: number;
  confidence_score: number;
    // Financial data
  estimated_capital: number;
  account_balance: number;
  account_priority_rank: number;
  
  // Timing fields for actions
  ready_for_execution: boolean;
  execution_window_start?: string | null;
  execution_window_end?: string | null;
  cooldown_until?: string | null;
  next_review_date?: string | null;
  last_action_date?: string | null;
  days_since_last_action?: number | null;
  lookback_days: number;
  urgency_level: string;
  decision_confidence: number;
  expires_at?: string | null;
  
  // Allocation timing fields
  allocation_ready_for_execution?: boolean;
  allocation_execution_window_start?: string | null;
  allocation_execution_window_end?: string | null;
  allocation_cooldown_until?: string | null;
  allocation_next_review_date?: string | null;
  allocation_last_action_date?: string | null;
  allocation_days_since_last_action?: number | null;
  allocation_urgency_level?: string;
  allocation_decision_confidence?: number;
  
  // Flags
  is_allocation_change: boolean;
  linked_to_demotion: boolean;
  linked_to_promotion: boolean;
  
  // Swap-related fields
  is_part_of_swap: boolean;
  swap_with?: string | null;
  swap_partner_strategy_name?: string | null;
  swap_group_id?: number | null;
  role_in_swap?: string | null;
  
  // Metadata
  rank: number;
  timestamp: string;
  generated_at: string;
  data_source: string;
  source: string;
  enriched: boolean;
  
  // UI Enhancement metadata (Phase 5)
  ui_metadata?: {
    category_color: string;
    category_icon: string;
    priority_badge: string;
    urgency_color: string;
    confidence_bar_color: string;
    expandable_section?: string;
    tooltip_text?: string;
  };
  
  // Completion tracking fields (Action Completion Feature)
  completion_tracking?: {
    core_action_completed: boolean;
    core_action_completed_at: string | null;
    core_action_completion_method: string | null;
    allocation_completed: boolean;
    allocation_completed_at: string | null;
    allocation_completion_method: string | null;
    balance_update_completed: boolean;
    balance_update_completed_at: string | null;
    balance_update_completion_method: string | null;
    overall_completion_status: 'pending' | 'partial' | 'complete';
    completion_notes: string | null;
    // Automation processing fields
    processed_for_effectiveness?: boolean;
    processing_mode?: string;
    processed_at?: string;
  };
}

// Updated interface for optimized priority actions response
export interface OptimizedPriorityActionsResponse {
  actions: PriorityAction[];
  total_actions: number;
  cached: boolean;
  timestamp: string;
}

export const getPriorityActions = async (params?: {
  strategy_name?: string;
  account_name?: string;
  force_refresh?: boolean;
}): Promise<PriorityAction[]> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.strategy_name) queryParams.append('strategy_name', params.strategy_name);
    if (params?.account_name) queryParams.append('account_name', params.account_name);
    if (params?.force_refresh) queryParams.append('force_refresh', 'true');
      const url = `/v1/portfolio/priority-actions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get<OptimizedPriorityActionsResponse>(url);
    return response.data.actions;
  } catch (error) {
    console.error('Error fetching priority actions:', error);
    throw error;
  }
};

// Additional API functions for the optimized portfolio system
export const getPriorityActionsOptimized = async (params?: {
  strategy_name?: string;
  account_name?: string;
  force_refresh?: boolean;
  include_detailed_rationale?: boolean;
  include_ui_metadata?: boolean;
}): Promise<OptimizedPriorityActionsResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.strategy_name) queryParams.append('strategy_name', params.strategy_name);
    if (params?.account_name) queryParams.append('account_name', params.account_name);
    if (params?.force_refresh) queryParams.append('force_refresh', 'true');
    if (params?.include_detailed_rationale) queryParams.append('include_detailed_rationale', 'true');
    if (params?.include_ui_metadata) queryParams.append('include_ui_metadata', 'true');
    
    // Use the enhanced endpoint that serves actionable recommendations from portfolio_recommendations table
    const url = `/v1/portfolio/priority-actions-enhanced${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get<OptimizedPriorityActionsResponse>(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching enhanced priority actions:', error);
    throw error;
  }
};

export const invalidatePortfolioCache = async (cache_types?: string[]): Promise<{ status: string; message: string }> => {
  try {
    const queryParams = new URLSearchParams();
    if (cache_types && cache_types.length > 0) {
      cache_types.forEach(type => queryParams.append('cache_types', type));
    }
      const url = `/v1/portfolio/invalidate-cache${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.post<{ status: string; message: string }>(url);
    return response.data;
  } catch (error) {
    console.error('Error invalidating portfolio cache:', error);
    throw error;
  }
};

export const getPortfolioCacheStatus = async (): Promise<{ [key: string]: any }> => {
  try {
    const response = await apiClient.get<{ [key: string]: any }>('/v1/portfolio/cache-status');
    return response.data;
  } catch (error) {
    console.error('Error fetching portfolio cache status:', error);
    throw error;
  }
};

// --- Portfolio Configuration ---

export interface PortfolioConfigCategories {
  [category: string]: string;
}

// Updated to match actual backend response structure
export interface CategoryConfigResponse {
  category: string;
  configs: {
    [key: string]: {
      value: any;
      default_value: any;
      description: string | null;
      validation_rules: any;
      has_user_override: boolean;
      user_config_name: string | null;
      display_unit?: string | null;
      unit_type?: string | null;
    };
  };
  has_any_overrides: boolean;
}

// Updated to match backend all configs response
export interface AllPortfolioConfigsResponse {
  lifecycle: {
    trade_count_thresholds: {
      value: any;
      default_value: any;
      description: string | null;
      validation_rules: any;
      has_user_override: boolean;
      user_config_name: string | null;
    };
    confidence_modifiers: {
      value: any;
      default_value: any;
      description: string | null;
      validation_rules: any;
      has_user_override: boolean;
      user_config_name: string | null;
    };
    allocation_caps: {
      value: any;
      default_value: any;
      description: string | null;
      validation_rules: any;
      has_user_override: boolean;
      user_config_name: string | null;
    };
  };
  account_moves: {
    promotion_thresholds: {
      value: any;
      default_value: any;
      description: string | null;
      validation_rules: any;
      has_user_override: boolean;
      user_config_name: string | null;
    };
    demotion_thresholds: {
      value: any;
      default_value: any;
      description: string | null;
      validation_rules: any;
      has_user_override: boolean;
      user_config_name: string | null;
    };
  };
  stability: {
    deviation_thresholds: {
      value: any;
      default_value: any;
      description: string | null;
      validation_rules: any;
      has_user_override: boolean;
      user_config_name: string | null;
    };
  };
  scoring: {
    component_weights: {
      value: any;
      default_value: any;
      description: string | null;
      validation_rules: any;
      has_user_override: boolean;
      user_config_name: string | null;
    };
  };
}

// Updated request payload structure
export interface UpdatePortfolioConfigRequest {
  value: any;
  config_name?: string;
}

export const getPortfolioConfigCategories = async (): Promise<PortfolioConfigCategories> => {
  const { data } = await apiClient.get<PortfolioConfigCategories>('/portfolio-config/categories');
  return data;
};

export const getPortfolioCategoryConfig = async (category: string): Promise<CategoryConfigResponse> => {
  const { data } = await apiClient.get<CategoryConfigResponse>(`/portfolio-config/${category}`);
  return data;
};

// Updated to get all configurations for dashboard overview
export const getAllPortfolioConfigs = async (): Promise<AllPortfolioConfigsResponse> => {
  const { data } = await apiClient.get<AllPortfolioConfigsResponse>('/portfolio-config/');
  return data;
};

export const updatePortfolioConfig = async (
  category: string, 
  key: string, 
  request: UpdatePortfolioConfigRequest
): Promise<{ message: string; category: string; key: string; value: any }> => {
  const { data } = await apiClient.put(`/portfolio-config/${category}/${key}`, request);
  return data;
};

export const resetPortfolioCategoryToDefaults = async (category: string): Promise<{ message: string; category: string }> => {
  const { data } = await apiClient.post<{ message: string; category: string }>(`/portfolio-config/reset/${category}`);
  return data;
};

// New function to delete user config override
export const deletePortfolioConfigOverride = async (
  category: string, 
  key: string
): Promise<{ message: string; category: string; key: string }> => {
  const { data } = await apiClient.delete(`/portfolio-config/${category}/${key}`);
  return data;
};

// --- Email Preferences Types ---
export interface EmailPreferences {
  user_id: string;
  educational_emails: boolean;
  feature_announcements: boolean;
  upsell_campaigns: boolean;
  milestone_celebrations: boolean;
  win_back_campaigns: boolean;
  daily_digest: boolean;
  weekly_digest: boolean;
  monthly_digest: boolean;
  marketing_emails: boolean;
  product_updates: boolean;
  security_alerts: boolean;
  billing_notifications: boolean;
  globally_unsubscribed: boolean;
  preferred_send_time: string;
  timezone: string;
  preferences_updated_at: string;
  active_subscriptions: string[];
}

export interface EmailPreferencesUpdate {
  educational_emails?: boolean;
  feature_announcements?: boolean;
  upsell_campaigns?: boolean;
  milestone_celebrations?: boolean;
  win_back_campaigns?: boolean;
  daily_digest?: boolean;
  weekly_digest?: boolean;
  monthly_digest?: boolean;
  marketing_emails?: boolean;
  product_updates?: boolean;
  preferred_send_time?: string;
  timezone?: string;
}

export interface UnsubscribeRequest {
  campaign_type?: string;
  reason?: string;
}

// --- Email Preferences API Functions ---
export const fetchEmailPreferences = async (): Promise<EmailPreferences> => {
  const { data } = await apiClient.get<EmailPreferences>('/email-preferences/');
  return data;
};

export const updateEmailPreferences = async (updates: EmailPreferencesUpdate): Promise<EmailPreferences> => {
  const { data } = await apiClient.put<EmailPreferences>('/email-preferences/', updates);
  return data;
};

export const unsubscribeFromAllEmails = async (reason?: string): Promise<{ message: string }> => {
  const { data } = await apiClient.post<{ message: string }>('/email-preferences/unsubscribe', { reason });
  return data;
};

export const resubscribeToEmails = async (): Promise<{ message: string }> => {
  const { data } = await apiClient.post<{ message: string }>('/email-preferences/resubscribe');
  return data;
};

// Portfolio recommendations generation
export interface RecommendationsGenerationResponse {
  status: string;
  task_id: string | null;
}

export const triggerRecommendationsGeneration = async (): Promise<RecommendationsGenerationResponse> => {
  try {
    console.log('Starting recommendations generation request...');
    const response = await apiClient.post<RecommendationsGenerationResponse>(
      '/portfolio-command/generate-recommendations',
      {},
      {
        timeout: 60000, // Increase timeout to 60 seconds for this specific request
      }
    );
    console.log('Recommendations generation response received:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error triggering recommendations generation:', error);
    if (error?.code === 'ECONNABORTED') {
      console.error('Request timed out after 60 seconds');
    }
    throw error;
  }
};

export interface SSETradeUpdate {
  event: string;
  data: {
    trade_count: number;
    recent_count: number;
    last_update: string;
    changes_detected: boolean;
    user_id: string;
  };
}

// Remove duplicate RefreshResponse interface
export interface RefreshResponse {
  success: boolean;
  message: string;
  timestamp: string;
  changes_detected: boolean;
  trade_count?: number;
  last_trade_time?: string;
  cache_invalidated: boolean;
}

// --- Action Completion Tracking API ---

export interface ActionCompletionRequest {
  action_identifier: string;  // strategy-account composite key
  completion_type: 'core_action' | 'allocation' | 'balance_update' | 'all';
  completion_method?: 'manual' | 'api' | 'automation';
  notes?: string;
}

export interface ActionCompletionResponse {
  success: boolean;
  message: string;
  updated_action: any;
  completion_summary: {
    completion_type?: string;
    completion_method?: string;
    action_taken?: string;
    completed_at?: string | null;
    overall_status?: string;
    core_action_completed: boolean;
    allocation_completed: boolean;
    balance_update_completed: boolean;
    overall_completion_status: 'pending' | 'partial' | 'complete';
    completion_progress: string;
  };
  // Subscription-aware fields from Phase 5 implementation
  bot_rules_update?: {
    success: boolean;
    message: string;
    action_type?: string;
  };
  cascade_cleanup?: {
    success: boolean;
    message: string;
    deleted_items?: string[];
  };
  operation_context?: {
    action_taken: 'marked' | 'unmarked';
    completion_type: string;
    bot_rules_operation?: any;
    cascade_cleanup?: any;
  };
}

export const markActionComplete = async (
  completionData: ActionCompletionRequest
): Promise<ActionCompletionResponse> => {
  try {
    console.log(`Marking action complete:`, completionData);
    const response = await apiClient.post<ActionCompletionResponse>(
      `/portfolio/recommendations/actions/complete`,
      completionData
    );
    console.log('Action completion response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error marking action as complete:', error);
    throw error;
  }
};

export const markActionCompleteById = async (
  completionData: {
    recommendation_id: string;
    completion_type: 'core_action' | 'allocation' | 'balance_update' | 'all';
    completion_method: string;
    notes?: string;
  }
): Promise<ActionCompletionResponse> => {
  try {
    console.log(`Marking action complete by ID:`, completionData);
    const response = await apiClient.post<ActionCompletionResponse>(
      `/portfolio/recommendations/actions/complete-by-id`,
      completionData
    );
    console.log('Action completion by ID response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error marking action as complete by ID:', error);
    throw error;
  }
};

export const toggleActionCompleteById = async (
  completionData: {
    recommendation_id: string;
    completion_type: 'core_action' | 'allocation' | 'balance_update' | 'all';
    completion_method: string;
    notes?: string;
  }
): Promise<ActionCompletionResponse> => {
  try {
    console.log(`Toggling action completion by ID:`, completionData);
    const response = await apiClient.post<ActionCompletionResponse>(
      `/portfolio/recommendations/actions/toggle-complete-by-id`,
      completionData
    );
    console.log('Action toggle completion by ID response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error toggling action completion by ID:', error);
    throw error;
  }
};

// Helper functions for completion tracking
export const hasAllocationChange = (action: PriorityAction): boolean => {
  return Math.abs(action.allocation_change || 0) >= 1;
};

export const hasBalanceUpdate = (action: PriorityAction): boolean => {
  // NEW: Check top-level balance update fields (supports new architecture)
  if (action.requires_balance_update === true) {
    return true;
  }
  
  // LEGACY: Check embedded in allocation rationale for backward compatibility
  return action.allocation_rationale_detailed?.balance_update?.required === true;
};

// NEW: Helper function for getting balance update timing information
export const getBalanceUpdateTiming = (action: PriorityAction) => {
  return {
    ready: action.balance_ready_for_execution ?? false,
    nextReview: action.balance_next_review_date,
    executionWindow: {
      start: action.balance_execution_window_start,
      end: action.balance_execution_window_end
    },
    urgency: action.balance_urgency_level ?? 'medium',
    confidence: action.balance_decision_confidence ?? 80,
    daysSinceLastAction: action.balance_days_since_last_action
  };
};

// Helper function for getting core action timing information
export const getActionTiming = (action: PriorityAction) => {
  return {
    ready: action.ready_for_execution ?? false,
    nextReview: action.next_review_date,
    executionWindow: {
      start: action.execution_window_start,
      end: action.execution_window_end
    },
    cooldownUntil: action.cooldown_until,
    urgency: action.urgency_level ?? 'medium',
    confidence: action.decision_confidence ?? 80
  };
};

// Helper function for getting allocation timing information
export const getAllocationTiming = (action: PriorityAction) => {
  return {
    ready: action.allocation_ready_for_execution ?? action.ready_for_execution ?? false,
    nextReview: action.allocation_next_review_date ?? action.next_review_date,
    executionWindow: {
      start: action.allocation_execution_window_start ?? action.execution_window_start,
      end: action.allocation_execution_window_end ?? action.execution_window_end
    },
    cooldownUntil: action.allocation_cooldown_until ?? action.cooldown_until,
    urgency: action.allocation_urgency_level ?? action.urgency_level ?? 'medium',
    confidence: action.allocation_decision_confidence ?? action.decision_confidence ?? 80
  };
};

export const getActionTypeLabel = (actionType: string): string => {
  const actionLabels: Record<string, string> = {
    'promote': 'Promote',
    'demote': 'Demote',
    'swap': 'Swap',
    'deactivate': 'Deactivate',
    'continue': 'Continue',
    'monitor_closely': 'Monitor',
    'initialize': 'Initialize',
    'balance_update': 'Balance Update'
  };
  
  return actionLabels[actionType.toLowerCase()] || actionType;
};

export const calculateOverallStatus = (action: PriorityAction): 'complete' | 'partial' | 'pending' => {
  if (!action.completion_tracking) {
    return 'pending';
  }
  
  const { completion_tracking } = action;
  const coreComplete = completion_tracking.core_action_completed;
  const allocationComplete = completion_tracking.allocation_completed;
  const balanceComplete = completion_tracking.balance_update_completed;
  
  // Check what's applicable
  const hasAllocation = hasAllocationChange(action);
  const hasBalance = hasBalanceUpdate(action);
  
  // Core action is only applicable for actionable items (not status-only actions)
  const needsCoreActionCompletion = !['continue', 'monitor_closely'].includes(action.action_type.toLowerCase());
  
  let totalApplicable = 0;
  let totalCompleted = 0;
  
  if (needsCoreActionCompletion) {
    totalApplicable += 1;
    if (coreComplete) totalCompleted += 1;
  }
  
  if (hasAllocation) {
    totalApplicable += 1;
    if (allocationComplete) totalCompleted += 1;
  }
  
  if (hasBalance) {
    totalApplicable += 1;
    if (balanceComplete) totalCompleted += 1;
  }
  
  // If no applicable items (e.g., status-only action with no allocation/balance changes), consider complete
  if (totalApplicable === 0) return 'complete';
  
  if (totalCompleted === totalApplicable) return 'complete';
  if (totalCompleted > 0) return 'partial';
  return 'pending';
};

// --- Dashboard Batch API Interfaces ---

/**
 * Dashboard initialization batch response
 * Contains all data needed for initial Dashboard load in a single request
 */
export interface DashboardInitResponse {
  pnl_timeframes: PnlTimeframes | null;
  account_summary: AccountPnlSummaryItem[] | null;
  strategy_summary: StrategyHealthMetricsItem[] | null;
  account_daily: AccountPnlDailyItem[] | null;
  strategy_daily: StrategyPnlDailyItem[] | null;
  monthly_calendar: MonthlyPnlItem[] | null;
  account_balances: BalancesResponse | null;
  account_balances_daily: AccountBalanceDailyItem[] | null;
  
  // Metadata
  timestamp: string;
  timezone: string;
  partial_failure: boolean;
  failed_sections: string[];
}

// --- Dashboard Batch API Function ---

/**
 * Fetches all Dashboard initial data in a single batch request.
 * 
 * This endpoint consolidates 8 individual API calls into one request,
 * significantly reducing Dashboard initial load time.
 * 
 * The response contains data that should be hydrated into individual
 * cache keys using queryClient.setQueryData() to preserve:
 * - SSE cache invalidation compatibility
 * - PureMath incremental update compatibility
 * - Cross-component cache sharing
 * 
 * @param userTimezone - User's IANA timezone (e.g., 'America/New_York')
 * @returns DashboardInitResponse with all Dashboard data sections
 */
export const getDashboardInit = async (userTimezone: string): Promise<DashboardInitResponse> => {
  console.log('[API] Fetching Dashboard batch data...');
  try {
    const response = await apiClient.get<DashboardInitResponse>('/v1/dashboard/init', {
      params: { user_timezone: userTimezone },
      timeout: 60000 // 60 second timeout for batch request
    });
    
    const { data } = response;
    
    // Log batch request result
    const successCount = [
      data.pnl_timeframes,
      data.account_summary,
      data.strategy_summary,
      data.account_daily,
      data.strategy_daily,
      data.monthly_calendar,
      data.account_balances,
      data.account_balances_daily
    ].filter(Boolean).length;
    
    console.log(`[API] Dashboard batch data fetched: ${successCount}/8 sections`, {
      partial_failure: data.partial_failure,
      failed_sections: data.failed_sections,
      timezone: data.timezone
    });
    
    return data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch Dashboard batch data';
    console.error('[API] Dashboard batch fetch error:', errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};